import { CdkScrollable } from '@angular/cdk/scrolling';
import { Component, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button'; // Adjust the path if necessary
import { MatOption } from '@angular/material/core';
import { MatDatepickerInput, MatDatepickerToggle, MatDatepicker } from '@angular/material/datepicker';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose,
} from '@angular/material/dialog';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { CommonIPLite, DST_PROP_BY_IP_TYPE, NestedSubType, UploadSubTypeGroupingMode } from '@model/ftf-api.model';
import { ApiService } from '@service/api.service';
import { SettingsService } from '@service/settings.service';

import { DocSubTypeMutiselectComponent } from '../../doc-sub-type-multiselect/doc-sub-type-multiselect.component';

export enum DateFilterType {
  Equal = 'eq',
  GreaterEq = 'ge',
  LessEq = 'le',
}
export interface TrifoldFilterDialogFormControl {
  search: FormControl<string>;
  type: FormControl<number[]>;
  date: FormControl<Date | null>;
  dateFilterType: FormControl<DateFilterType>;
}
export interface TrifoldFilterDialogFormData {
  search: string;
  type: number[];
  date: Date | null;
  dateFilterType: DateFilterType;
}
export interface TrifoldFilterDialogData {
  item: CommonIPLite;
  oldValue: TrifoldFilterDialogFormData;
}
export const DEFAULT_TRIFOLD_FILTER_STATE: TrifoldFilterDialogFormData = {
  search: '',
  date: null,
  type: [],
  dateFilterType: DateFilterType.Equal,
};

/**
 * Description: Dialog component for filtering trifold items.
 *
 * @export
 * @class TrifoldFilterDialogComponent
 * @typedef {TrifoldFilterDialogComponent}
 */
@Component({
  selector: 'trifold-filter-dialog',
  templateUrl: './trifold-filter-dialog.component.html',
  styleUrl: './trifold-filter-dialog.component.scss',
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    DocSubTypeMutiselectComponent,
    MatSelect,
    MatOption,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatDialogActions,
    MatButton,
    MatDialogClose,
  ],
})
export class TrifoldFilterDialogComponent {
  private fb = inject(FormBuilder);
  private apiService = inject(ApiService);
  private settingsService = inject(SettingsService);
  dialogRef = inject<MatDialogRef<TrifoldFilterDialogComponent>>(MatDialogRef);
  private data = inject<TrifoldFilterDialogData>(MAT_DIALOG_DATA);

  form: FormGroup<TrifoldFilterDialogFormControl>;
  subTypes: NestedSubType = {};

  /**
   * Creates an instance of TrifoldFilterDialogComponent.
   *
   * @remarks
   * The constructor initializes the form controls and fetches document sub-types from the API.
   * It organizes the sub-types based on the user's settings for grouping mode (by document type or flat).
   * The form is then patched with any existing filter values passed in through the dialog data.
   */
  constructor() {
    const { data } = this;

    this.form = this.fb.group<TrifoldFilterDialogFormControl>({
      search: this.fb.control<string>('', { nonNullable: true }),
      type: this.fb.control<number[]>([], { nonNullable: true }),
      date: this.fb.control(null),
      dateFilterType: this.fb.control<DateFilterType>(DateFilterType.Equal, { nonNullable: true }),
    });
    this.apiService.findDocumentSubTypes().subscribe((subTypes) => {
      this.subTypes = {};
      // bucketise our subtypes based on doc type

      // Get grouping mode from settings
      const settings = this.settingsService.getSettings();
      const groupingMode = settings.uploadSubTypeGroupingMode;
      const useDocTypeGrouping = groupingMode === 'Doc Type';

      if (useDocTypeGrouping) {
        // Group by document type (current behavior)
        this.subTypes = {}; // Object format for grouped mode
        subTypes.forEach((type) => {
          const docType = type.doctypeName!;
          const include = type[DST_PROP_BY_IP_TYPE[data.item.ipType]] || data.oldValue.type.includes(type.id!);
          if (!include) return;
          if (!this.subTypes[docType]) {
            this.subTypes[docType] = [];
          }
          this.subTypes[docType].push(type);
        });
      } else {
        // For flat mode, provide a flat array directly
        const filteredTypes = subTypes
          .filter((type) => type[DST_PROP_BY_IP_TYPE[data.item.ipType]] || data.oldValue.type.includes(type.id!))
          .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));

        this.subTypes = { 'All Sub-Types': filteredTypes };
      }

      this.form.patchValue(data.oldValue);
    });
  }

  /**
   * Gets whether the document sub-types are grouped by document type.
   *
   * @readonly
   * @type {boolean}
   */
  get useDocTypeGrouping(): boolean {
    const settings = this.settingsService.getSettings();
    return settings.uploadSubTypeGroupingMode === UploadSubTypeGroupingMode.DocType;
  }

  /**
   * Handles the Clear button click event.
   */
  onClearClick(): void {
    this.form.patchValue(DEFAULT_TRIFOLD_FILTER_STATE);
    this.dialogRef.close({ clear: true });
  }

  /**
   * Handles the Cancel button click event.
   */
  onCancelClick(): void {
    this.dialogRef.close();
  }
}
